import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { getDesktopBridge } from '@/lib/desktop/bridge';
import { getWebAuthRedirectUrl } from '@/lib/mobile/environment';

import { NATIVE_OAUTH_FINISHED_EVENT } from './use-deep-link-auth';

export type SocialProvider = 'google' | 'azure';

interface UseSocialSignInReturn {
  signIn: (provider?: SocialProvider) => Promise<void>;
  loadingProvider: SocialProvider | null;
  isLoading: boolean;
  /** Abandon a browser handoff the user has given up on. Safe to call when idle. */
  cancelSignIn: () => void;
  /**
   * A handoff was abandoned rather than completed, so the surface can say so.
   * Cleared by the next attempt and by a callback that does arrive.
   */
  handoffAbandoned: boolean;
}

/**
 * How long a browser handoff may sit pending before the shell takes its controls
 * back.
 *
 * Cancellation is UNDETECTABLE here: the user closes a browser tab and nothing
 * is sent back, so there is no event to listen for. Without a bound, one press
 * disables every sign-in method until the app is restarted - the same latch
 * shape as a WebAuthn promise that never settles.
 *
 * Two minutes is sized to outlast a real sign-in, not to feel responsive: a
 * consent screen plus a password plus an MFA prompt on a slow connection is
 * comfortably a minute, and the cheaper recoveries below (an explicit Cancel,
 * and regaining focus) cover the impatient case long before this fires. This is
 * the backstop for a user who walked away.
 */
const OAUTH_HANDOFF_TIMEOUT_MS = 120_000;

/**
 * How long to wait after the window regains focus before calling a pending
 * handoff abandoned.
 *
 * Focus alone does not mean the user gave up - the callback URL brings the app
 * forward itself, so a SUCCESSFUL sign-in also regains focus, moments before its
 * event lands. Resetting immediately would therefore cancel the flows that
 * worked. The grace lets that event win the race; only a window that comes back
 * and then stays quiet is treated as a user who closed the tab.
 */
const FOCUS_GRACE_MS = 5000;

const PROVIDER_LABEL: Record<SocialProvider, string> = {
  google: 'Google',
  azure: 'Microsoft',
};
const FAILED_TITLE_KEY = 'oauth.failedTitle';
const UNABLE_TO_START_KEY = 'oauth.unableToStart';

export function useSocialSignIn(defaultProvider?: SocialProvider): UseSocialSignInReturn {
  const [loadingProvider, setLoadingProvider] = useState<SocialProvider | null>(null);
  const { t } = useTranslation('auth');
  const { toast } = useToast();
  const oauthInFlightRef = useRef(false);
  const [handoffAbandoned, setHandoffAbandoned] = useState(false);
  const abandonTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const graceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Both recovery timers, dropped together. Every exit from a pending handoff
   * runs this - a timer that outlives its attempt would take the controls off a
   * LATER one, which is the bug this whole change exists to remove, reintroduced
   * from the other side.
   */
  const clearRecoveryTimers = useCallback(() => {
    if (abandonTimerRef.current !== null) {
      clearTimeout(abandonTimerRef.current);
      abandonTimerRef.current = null;
    }
    if (graceTimerRef.current !== null) {
      clearTimeout(graceTimerRef.current);
      graceTimerRef.current = null;
    }
  }, []);

  const clearOAuthLoading = useCallback(() => {
    clearRecoveryTimers();
    oauthInFlightRef.current = false;
    setLoadingProvider(null);
    setHandoffAbandoned(false);
  }, [clearRecoveryTimers]);

  /**
   * Give the controls back on a handoff nobody is coming back from.
   *
   * The three ways in are the three things we CAN observe, since the one we
   * cannot is the cancel itself: the user says so, the window comes back and
   * stays quiet, or the clock runs out. All three mean the same thing to this
   * hook, so they share an exit - and it is deliberately distinct from
   * `clearOAuthLoading`, which means the callback actually landed.
   */
  const abandonHandoff = useCallback(() => {
    if (!oauthInFlightRef.current) {
      return;
    }
    clearRecoveryTimers();
    oauthInFlightRef.current = false;
    setLoadingProvider(null);
    setHandoffAbandoned(true);
  }, [clearRecoveryTimers]);

  useEffect(
    () => () => {
      clearRecoveryTimers();
      oauthInFlightRef.current = false;
    },
    [clearRecoveryTimers],
  );

  useEffect(() => {
    if (!getDesktopBridge()) {
      return;
    }

    globalThis.window.addEventListener(NATIVE_OAUTH_FINISHED_EVENT, clearOAuthLoading);
    return () => {
      globalThis.window.removeEventListener(NATIVE_OAUTH_FINISHED_EVENT, clearOAuthLoading);
    };
  }, [clearOAuthLoading]);

  /**
   * Focus-regain recovery, armed only while a handoff is actually pending.
   *
   * Registering unconditionally would leave a listener on the window for every
   * mounted login surface; more importantly, arming it on `loadingProvider`
   * means the listener is torn down the instant a callback lands, so it cannot
   * fire against a flow that already finished.
   */
  useEffect(() => {
    if (loadingProvider === null) {
      return;
    }
    const onFocus = (): void => {
      if (graceTimerRef.current !== null) {
        return;
      }
      graceTimerRef.current = setTimeout(() => {
        graceTimerRef.current = null;
        abandonHandoff();
      }, FOCUS_GRACE_MS);
    };
    globalThis.window.addEventListener('focus', onFocus);
    return () => {
      globalThis.window.removeEventListener('focus', onFocus);
    };
  }, [abandonHandoff, loadingProvider]);

  const signIn = useCallback(
    async (providerArg?: SocialProvider) => {
      const provider = providerArg ?? defaultProvider;
      if (!provider || oauthInFlightRef.current) {
        return;
      }

      clearRecoveryTimers();
      oauthInFlightRef.current = true;
      setLoadingProvider(provider);
      setHandoffAbandoned(false);
      let keepLoading = false;
      try {
        const desktop = getDesktopBridge();
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider,
          options: {
            redirectTo: desktop?.authCallbackUrl ?? getWebAuthRedirectUrl(),
            ...(provider === 'azure' ? { scopes: 'openid profile email' } : {}),
            ...(desktop ? { skipBrowserRedirect: true } : {}),
            // Always show the account chooser. Without it, a browser holding
            // exactly one provider session silently signs that account in -
            // on a shared office device that is the WRONG default, and both
            // Google and Microsoft honour the same parameter.
            queryParams: { prompt: 'select_account' },
          },
        });

        if (error) {
          toast({
            title: t(FAILED_TITLE_KEY),
            description: t(UNABLE_TO_START_KEY, { provider: PROVIDER_LABEL[provider] }),
            variant: 'destructive',
          });
          return;
        }

        if (desktop) {
          if (!data.url || !(await desktop.openOAuth(data.url))) {
            toast({
              title: t(FAILED_TITLE_KEY),
              description: t(UNABLE_TO_START_KEY, { provider: PROVIDER_LABEL[provider] }),
              variant: 'destructive',
            });
            return;
          }
          // Armed HERE, not at the top of `signIn`: before this point the flow
          // can still fail loudly and reset itself. From here the shell is
          // waiting on something that may never answer.
          abandonTimerRef.current = setTimeout(abandonHandoff, OAUTH_HANDOFF_TIMEOUT_MS);
          keepLoading = true;
        }
      } catch {
        console.error('OAuth sign-in failed');
        toast({
          title: t(FAILED_TITLE_KEY),
          description: t('oauth.unexpectedDescription'),
          variant: 'destructive',
        });
      } finally {
        if (!keepLoading) {
          oauthInFlightRef.current = false;
          setLoadingProvider(null);
        }
      }
    },
    [abandonHandoff, clearRecoveryTimers, defaultProvider, t, toast],
  );

  return {
    signIn,
    loadingProvider,
    isLoading: loadingProvider !== null,
    cancelSignIn: abandonHandoff,
    handoffAbandoned,
  };
}
